import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Navbar } from '../../components/feature/Navbar';
import { Footer } from '../../components/feature/Footer';
import { WhatsAppButton } from '../../components/feature/WhatsAppButton';
import { ListingCard } from '../../components/base/ListingCard';
import { mockListings } from '../../mocks/listings';
import { mockFacilities } from '../../mocks/facilities';

const ListingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedFacility, setSelectedFacility] = useState(searchParams.get('facility') || '');
  const [selectedLocation, setSelectedLocation] = useState(searchParams.get('location') || '');
  const [selectedWeekCount, setSelectedWeekCount] = useState(searchParams.get('weeks') || '');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('newest');

  const locations = Array.from(new Set(mockListings.map(l => l.location)));
  const facilities = Array.from(new Set(mockListings.map(l => l.facility_name)));

  const filteredListings = mockListings.filter(listing => {
    const matchesSearch = !searchQuery || 
      listing.facility_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      listing.location.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFacility = !selectedFacility || listing.facility_name === selectedFacility;
    const matchesLocation = !selectedLocation || listing.location === selectedLocation;
    const matchesWeekCount = !selectedWeekCount || 
      (selectedWeekCount === '4' ? listing.week_count >= 4 : listing.week_count === parseInt(selectedWeekCount));
    
    const matchesPrice = (!priceRange.min || !listing.price || listing.price >= parseInt(priceRange.min)) &&
                        (!priceRange.max || !listing.price || listing.price <= parseInt(priceRange.max));

    return matchesSearch && matchesFacility && matchesLocation && matchesWeekCount && matchesPrice;
  });

  const sortedListings = [...filteredListings].sort((a, b) => {
    switch (sortBy) {
      case 'price-asc':
        return (a.price || 0) - (b.price || 0);
      case 'price-desc':
        return (b.price || 0) - (a.price || 0);
      case 'weeks':
        return b.week_count - a.week_count;
      default:
        return b.id - a.id;
    }
  });

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedFacility('');
    setSelectedLocation('');
    setSelectedWeekCount('');
    setPriceRange({ min: '', max: '' });
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#0A1628] to-[#1a2d4d]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Devremülk İlanları
          </h1>
          <p className="text-xl text-white/80 max-w-3xl">
            Türkiye'nin en prestijli tatil köylerinde devremülk fırsatları
          </p>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-[#E5E9F0] p-6 sticky top-28">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#0A1628]">Filtreler</h2>
                <button
                  onClick={handleClearFilters}
                  className="text-sm text-[#D4AF37] hover:underline cursor-pointer"
                >
                  Temizle
                </button>
              </div>

              <div className="space-y-6">
                {/* Search */}
                <div>
                  <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                    Ara
                  </label>
                  <input
                    type="text"
                    placeholder="Tesis veya lokasyon..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-3 text-sm text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors"
                  />
                </div>

                {/* Facility */}
                <div>
                  <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                    Tesis
                  </label>
                  <select
                    value={selectedFacility}
                    onChange={(e) => setSelectedFacility(e.target.value)}
                    className="w-full px-4 py-3 text-sm text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors cursor-pointer"
                  >
                    <option value="">Tüm Tesisler</option>
                    {facilities.map(facility => (
                      <option key={facility} value={facility}>{facility}</option>
                    ))}
                  </select>
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                    Lokasyon
                  </label>
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="w-full px-4 py-3 text-sm text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors cursor-pointer"
                  >
                    <option value="">Tüm Lokasyonlar</option>
                    {locations.map(location => (
                      <option key={location} value={location}>{location}</option>
                    ))}
                  </select>
                </div>

                {/* Week Count */}
                <div>
                  <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                    Hafta Sayısı
                  </label>
                  <div className="space-y-2">
                    {['1', '2', '3', '4'].map(week => (
                      <label key={week} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="weekCount"
                          value={week}
                          checked={selectedWeekCount === week}
                          onChange={(e) => setSelectedWeekCount(e.target.value)}
                          className="w-4 h-4 text-[#D4AF37] cursor-pointer"
                        />
                        <span className="text-sm text-[#0A1628]">
                          {week === '4' ? '4+ Hafta' : `${week} Hafta`}
                        </span>
                      </label>
                    ))}
                    {selectedWeekCount && (
                      <button
                        onClick={() => setSelectedWeekCount('')}
                        className="text-sm text-[#D4AF37] hover:underline cursor-pointer"
                      >
                        Temizle
                      </button>
                    )}
                  </div>
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                    Fiyat Aralığı
                  </label>
                  <div className="space-y-2">
                    <input
                      type="number"
                      placeholder="Min ₺"
                      value={priceRange.min}
                      onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                      className="w-full px-4 py-3 text-sm text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors"
                    />
                    <input
                      type="number"
                      placeholder="Max ₺"
                      value={priceRange.max}
                      onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                      className="w-full px-4 py-3 text-sm text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Listings Grid */}
          <div className="lg:col-span-3">
            {/* Sort & Count */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <p className="text-base text-[#6B7A90]">
                <strong className="text-[#0A1628]">{sortedListings.length}</strong> ilan bulundu
              </p>
              <div className="flex items-center gap-3">
                <label className="text-sm text-[#6B7A90] whitespace-nowrap">Sırala:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 text-sm text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors cursor-pointer"
                >
                  <option value="newest">En Yeni</option>
                  <option value="price-asc">Fiyat (Düşük-Yüksek)</option>
                  <option value="price-desc">Fiyat (Yüksek-Düşük)</option>
                  <option value="weeks">Hafta Sayısı</option>
                </select>
              </div>
            </div>

            {/* Listings Grid */}
            {sortedListings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                {sortedListings.map((listing) => (
                  <ListingCard key={listing.id} {...listing} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-[#F7F9FC] rounded-2xl">
                <i className="ri-search-line text-6xl text-[#E5E9F0] mb-4"></i>
                <p className="text-xl text-[#6B7A90] mb-4">
                  Aradığınız kriterlere uygun ilan bulunamadı
                </p>
                <button
                  onClick={handleClearFilters}
                  className="text-[#D4AF37] font-medium hover:underline cursor-pointer"
                >
                  Filtreleri Temizle
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ListingsPage;